import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { languages } from "../i18n/translations";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "./ui/dropdown-menu";
import { Globe, Menu, X, LayoutDashboard, LogOut, User } from "lucide-react";
import { LOGO_URL } from "../lib/assets";

const Logo = () => (
  <Link to="/" className="flex items-center gap-2 group">
    <img
      src={LOGO_URL}
      alt="ScoutOS"
      className="w-9 h-9 rounded-xl object-cover shadow-lg shadow-blue-500/20 group-hover:scale-105 transition-transform"
    />
    <span className="font-display text-xl font-bold tracking-tight text-white">
      Scout<span className="text-blue-500">OS</span>
    </span>
  </Link>
);

const Navbar = () => {
  const { t, lang, setLang, user, logout, loginWithGoogle } = useApp();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const currentLang = languages.find((l) => l.code === lang);

  const navLinks = [
    { to: "/search", label: t.nav.search },
    { to: "/about", label: t.nav.about },
    { to: "/pricing", label: t.nav.pricing },
    { to: "/contact", label: t.nav.contact },
  ];

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const handleLogin = () => {
    loginWithGoogle();
  };

  return (
    <header className="fixed top-0 inset-x-0 z-50 border-b border-white/5 bg-[#08090b]/80 backdrop-blur-xl">
      <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between">
        <Logo />

        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="text-sm text-gray-400 hover:text-white transition-colors"
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-1.5 text-sm text-gray-300 hover:text-white transition-colors px-2 py-1.5 rounded-lg hover:bg-white/5">
                <Globe className="w-4 h-4" />
                <span>{currentLang?.label}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#111318] border-white/10 text-gray-200">
              {languages.map((l) => (
                <DropdownMenuItem
                  key={l.code}
                  onClick={() => setLang(l.code)}
                  className={`cursor-pointer focus:bg-white/10 ${lang === l.code ? "text-blue-400" : ""}`}
                >
                  <span className="mr-2">{l.flag}</span> {l.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 pl-1 pr-3 py-1 rounded-full bg-white/5 hover:bg-white/10 transition-colors">
                  <span className="w-7 h-7 rounded-full bg-blue-600 text-white text-xs font-semibold flex items-center justify-center">
                    {user.name?.[0]?.toUpperCase() || "U"}
                  </span>
                  <span className="text-sm text-gray-200 max-w-[100px] truncate">{user.name}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-[#111318] border-white/10 text-gray-200 w-48">
                <DropdownMenuLabel className="text-gray-400 font-normal text-xs truncate">{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem onClick={() => navigate("/dashboard")} className="cursor-pointer focus:bg-white/10">
                  <LayoutDashboard className="w-4 h-4 mr-2" /> {t.nav.dashboard}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout} className="cursor-pointer focus:bg-white/10 text-red-400">
                  <LogOut className="w-4 h-4 mr-2" /> {t.nav.logout}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <button onClick={handleLogin} className="text-sm text-gray-300 hover:text-white transition-colors">
                {t.nav.login}
              </button>
              <Button
                onClick={handleLogin}
                className="bg-blue-600 hover:bg-blue-500 text-white rounded-full h-9 px-5 text-sm font-medium"
              >
                {t.nav.signup}
              </Button>
            </>
          )}
        </div>

        <button className="md:hidden text-gray-200" onClick={() => setOpen(!open)}>
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-white/5 bg-[#0b0d11] px-5 py-4 space-y-3">
          {navLinks.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              className="block text-gray-300 py-1.5"
            >
              {l.label}
            </Link>
          ))}
          <div className="flex gap-2 pt-2">
            {languages.map((l) => (
              <button
                key={l.code}
                onClick={() => setLang(l.code)}
                className={`text-sm px-3 py-1.5 rounded-lg border ${lang === l.code ? "border-blue-500 text-blue-400" : "border-white/10 text-gray-400"}`}
              >
                {l.flag} {l.code.toUpperCase()}
              </button>
            ))}
          </div>
          <div className="pt-2 flex flex-col gap-2">
            {user ? (
              <>
                <Button onClick={() => { setOpen(false); navigate("/dashboard"); }} variant="outline" className="border-white/10 text-gray-200">
                  <User className="w-4 h-4 mr-2" /> {t.nav.dashboard}
                </Button>
                <Button onClick={() => { setOpen(false); handleLogout(); }} variant="outline" className="border-white/10 text-red-400">
                  {t.nav.logout}
                </Button>
              </>
            ) : (
              <>
                <Button onClick={() => { setOpen(false); handleLogin(); }} variant="outline" className="border-white/10 text-gray-200">
                  {t.nav.login}
                </Button>
                <Button onClick={() => { setOpen(false); handleLogin(); }} className="bg-blue-600 hover:bg-blue-500 text-white">
                  {t.nav.signup}
                </Button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
